import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import { getIntegrationAppLabel } from 'helpers';
import {
  Integration_FieldsFragment,
  IntegrationApp,
  useIntegration_GetIntegrationsByPortalIdQuery,
} from 'queries';

import AddIntegrationModal from './add-integration-modal';
import { IntegrationActions } from './integration-actions';
import { useStyles } from './integration-list.styles';

export const IntegrationList = () => {
  const classes = useStyles();
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const { portalId } = useParams<Record<string, string>>();

  const { data: integrationData, loading: integrationDataLoading } =
    useIntegration_GetIntegrationsByPortalIdQuery({
      variables: {
        input: {
          portalId: portalId,
        },
      },
    });

  const usedIntegrationOptions = integrationData?.data?.map((integration) => integration.app) ?? [];
  const availableIntegrationOptions = Object.values(IntegrationApp).filter(
    (option) => !usedIntegrationOptions.includes(option),
  );

  // const getWebhookEndpointUrl = (app: IntegrationApp) => {
  //   switch (app) {
  //     case IntegrationApp.DecisionVault:
  //       return `https://api.trustate.com/api/decision-vault/webhooks?portalId=${portalId}`;
  //     default:
  //       return '';
  //   }
  // };

  const columns: GridColDef<Integration_FieldsFragment>[] = [
    {
      disableColumnMenu: true,
      field: 'app',
      filterable: false,
      flex: 1,
      headerName: 'App',
      renderHeader: () => <Typography>App</Typography>,
      renderCell: ({ row }) => {
        return <Typography>{getIntegrationAppLabel(row.app)}</Typography>;
      },
    },
    {
      disableColumnMenu: true,
      field: 'authType',
      filterable: false,
      flex: 1,
      headerName: 'Auth Type',
      renderHeader: () => <Typography>Auth Type</Typography>,
      renderCell: ({ row }) => {
        return <Typography>{row.authType}</Typography>;
      },
    },
    // {
    //   disableColumnMenu: true,
    //   field: 'expiresAt',
    //   filterable: false,
    //   flex: 1,
    //   headerName: 'Expires At',
    //   renderHeader: () => <Typography>Expires At</Typography>,
    //   renderCell: ({ row }) => {
    //     return <Typography>{row.oAuthExpiresAt}</Typography>;
    //   },
    // },
    // disabled until we determine if we want webhook integrations with DecisionVault
    // {
    //   disableColumnMenu: true,
    //   field: 'webhooks',
    //   filterable: false,
    //   flex: 1,
    //   headerName: 'Webhooks',
    //   renderHeader: () => <Typography>Webhooks</Typography>,
    //   renderCell: ({ row }) => {
    //     const webhookUrl = getWebhookEndpointUrl(row.app);
    //     return (
    //       <Box>
    //         <Tooltip title="Copy webhook endpoint" key="copyWebhookEndpoint">
    //           <IconButton
    //             aria-label="copyWebhookEndpoint"
    //             onClick={() => {
    //               navigator.clipboard.writeText(webhookUrl);
    //               showToast({
    //                 message: 'Webhook endpoint copied to clipboard',
    //                 severity: NotificationSeverity.SUCCESS,
    //               });
    //             }}
    //             size="medium"
    //           >
    //             <ContentCopyIcon />
    //           </IconButton>
    //         </Tooltip>
    //       </Box>
    //     );
    //   },
    // },
    {
      disableColumnMenu: true,
      field: 'actions',
      type: 'actions',
      filterable: false,
      flex: 1,
      headerName: 'Actions',
      renderHeader: () => <Typography>Actions</Typography>,
      renderCell: ({ row }) => (
        <Box>
          <IntegrationActions integrationId={row.id} />
        </Box>
      ),
    },
  ];

  const rows = integrationData?.data || [];

  return (
    <Box className={classes.container}>
      {showAddIntegrationModal && (
        <AddIntegrationModal
          showModal={showAddIntegrationModal}
          setShowModal={setShowAddIntegrationModal}
          availableIntegrationOptions={availableIntegrationOptions}
        />
      )}
      <Box>
        <Box className={classes.button}>
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setShowAddIntegrationModal(true);
            }}
          >
            + Add Integration
          </Button>
        </Box>
      </Box>

      <DataGrid
        className={classes.dataGrid}
        loading={integrationDataLoading}
        columns={columns}
        rows={rows}
        disableColumnReorder
        disableRowSelectionOnClick
        disableColumnMenu
        columnHeaderHeight={45}
        autoHeight={true}
        pagination
        pageSizeOptions={[5, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
        }}
        slots={{
          toolbar: () => (
            <Box>
              <Typography variant="h6">Integrations</Typography>
              <hr />
            </Box>
          ),
        }}
      />
    </Box>
  );
};
